import React, { useEffect } from 'react'
import papa from 'papaparse'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { format } from 'date-fns'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  useStateWithDebounce,
  useInsuranceTestsResultsAnalytics,
  Lab,
  TestSessionPaymentStatus,
  InsuranceTestsResultsAnalytics as InsuranceTestsResultsAnalyticsCsv,
  InsuranceTestsResultsAnalyticsVariables as InsuranceTestsResultsAnalyticsVariablesCsv,
  UserPermissions,
  useAuth,
  SortByType,
  InsuranceTestsResultsAnalytics_insuranceTestsResultsAnalytics_tests,
} from '@modmd/data'
import { Downloading } from 'components/Downloading'
import { useSortChangeHandler } from 'utils/helpers'
import { ROUTES } from 'internal-portal/constants/routes'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import { RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import { LAB_OPTIONS } from '../TestSessions/utils'
import { INSURANCE_TESTS_RESULTS_ANALYTICS } from './operations'
import { InsuranceAnalyticsTestsResultsDatalist } from 'sharedComponents/InsuranceAnalyticsTestsResultsDatalist'

type AnalyticsTestsResultsProps = RouteComponentProps

type FilterState = {
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  companyOrGroupLike?: string
  groupId?: string
  companyId?: string
  lab?: Lab | null
  resultsPerPage?: string
}

const InsuranceAnalyticsTestsResults: React.VFC<AnalyticsTestsResultsProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const history = useHistory()
  const searchState = useSearchState<FilterState>()
  const { sorter, handleSortChange } = useSortChangeHandler()
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const [groupedDownloadableDataUrl, setGroupedDownloadableDataUrl] = React.useState<string>()
  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const [download, setDownload] = React.useState(false)
  const [downloadGrouped, setDownloadGrouped] = React.useState(false)
  const [getAnalyticsData, testsQuery] = useInsuranceTestsResultsAnalytics()
  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const pageLength = Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value)
  const page = Number(searchState.data.page) || 1

  const getData = () => {
    getAnalyticsData({
      variables: {
        filterData: {
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
          lab: searchState.data.lab,
        },
        pagination: {
          from: pageLength * (page - 1),
          length: pageLength,
          sortBy: sorter?.sortBy || SortByType.RESULT_RECIEVED_AT,
          direction: sorter?.direction || SortDirectionEnum.ASC,
        },
      },
    })
  }

  useEffect(() => {
    void getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const [getTestsCsv, { data: testsCsv, loading: testsCsvLoading }] = useLazyQuery<
    InsuranceTestsResultsAnalyticsCsv,
    InsuranceTestsResultsAnalyticsVariablesCsv
  >(INSURANCE_TESTS_RESULTS_ANALYTICS)

  const [
    getGroupedTestsCsv,
    { data: groupedTestsCsv, loading: groupedTestsCsvLoading },
  ] = useLazyQuery<InsuranceTestsResultsAnalyticsCsv, InsuranceTestsResultsAnalyticsVariablesCsv>(
    INSURANCE_TESTS_RESULTS_ANALYTICS
  )

  React.useEffect(() => {
    if (download) {
      const testResult = testsCsv?.insuranceTestsResultsAnalytics.tests.map(
        (test: InsuranceTestsResultsAnalytics_insuranceTestsResultsAnalytics_tests) => {
          const {
            barcode,
            resultProvisionedAt,
            TestSession,
            testedAt,
            Insurance,
            Company,
            Group,
            User,
            insuranceStatus,
            insuranceStatusWhenAdded,
            insuranceUpdatedAt,
          } = test
          const patient = `${User.firstName || ''} ${User.lastName || ''}`
          const testedAtDate = getFormattedDateAndTime(testedAt, true)
          const resultDate = getFormattedDateAndTime(resultProvisionedAt, true)
          const paymentStatus =
            TestSession.paymentStatus || 'Invoicing'
              ? TestSession.paymentStatus === TestSessionPaymentStatus.Insurance
                ? 'Insurance'
                : 'Invoice'
              : 'Credit card'

          const testResult = {
            'Session Id': TestSession.id,
            'Session Date': format(new Date(TestSession.date), 'dd/MM/yyyy'),
            'Company Name': Company?.name || '',
            'Group Name': Group?.name || '',
            'Test Type': TestSession.type,
            Lab: TestSession.lab,
            'Payment Status': paymentStatus,
            'Insurance Name': Insurance?.insuranceCompany || '',
            'Insurance Status': insuranceStatus,
            Barcode: barcode,
            Patient: patient,
            'Specimen Collected At': testedAtDate,
            'Result Date': resultDate,
            'Insurance Status When Tested': insuranceStatusWhenAdded,
            'Insurance Updated At': getFormattedDateAndTime(insuranceUpdatedAt, true),
          }
          return testResult
        }
      )
      if (testResult && !downloadableDataUrl) {
        const csv = papa.unparse(testResult, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Tests-Results-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownload(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
        setDownload(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testsCsv])

  React.useEffect(() => {
    if (downloadGrouped) {
      const tests = groupedTestsCsv?.insuranceTestsResultsAnalytics.tests || []

      const groupedCount: { 'Insurance Company': string; 'Approved Count': number }[] = []

      for (const test of tests) {
        if (test.insuranceStatus !== 'Approved') continue

        const insuranceCompany = test.Insurance?.insuranceCompany || ''

        if (insuranceCompany) {
          const existingEntry = groupedCount.find(
            (entry) => entry['Insurance Company'] === insuranceCompany
          )

          if (existingEntry) {
            existingEntry['Approved Count'] = Number(existingEntry['Approved Count']) + 1
          } else {
            groupedCount.push({ 'Insurance Company': insuranceCompany, 'Approved Count': 1 })
          }
        }
      }

      if (groupedCount.length > 0 && !groupedDownloadableDataUrl) {
        const csv = papa.unparse(groupedCount, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Insurance-Company-Count-Results-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownloadGrouped(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setGroupedDownloadableDataUrl('')
        setDownloadGrouped(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedTestsCsv])

  const handleDownloadCsv = () => {
    getTestsCsv({
      variables: {
        pagination: {
          from: 0,
          length:
            testsQuery.data?.insuranceTestsResultsAnalytics.pagination.total ||
            Number(RESULTS_PER_PAGE[0].value),
          sortBy: 'id',
        },
        filterData: {
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          lab: searchState.data.lab,
        },
      },
    })
    setDownload(true)
  }

  const handleDownloadGroupedCsv = () => {
    getGroupedTestsCsv({
      variables: {
        pagination: {
          from: 0,
          length:
            testsQuery.data?.insuranceTestsResultsAnalytics.pagination.total ||
            Number(RESULTS_PER_PAGE[0].value),
          sortBy: 'id',
        },
        filterData: {
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          lab: searchState.data.lab,
        },
      },
    })
    setDownloadGrouped(true)
  }

  return (
    <DashboardPage
      Header={
        hasPermission([UserPermissions.DOWNLOAD_TEST_RESULTS_ANALYTICS_CSV]) && (
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <Button
              appearance="filled"
              onClick={handleDownloadGroupedCsv}
              disabled={groupedTestsCsvLoading}
              mr="1rem"
            >
              {groupedTestsCsvLoading ? <Downloading /> : 'Download count by insurance company'}
            </Button>
            <Button appearance="filled" onClick={handleDownloadCsv} disabled={testsCsvLoading}>
              {testsCsvLoading ? <Downloading /> : 'Download csv'}
            </Button>
          </Box>
        )
      }
    >
      <Container>
        <InsuranceAnalyticsTestsResultsDatalist
          tests={testsQuery.data?.insuranceTestsResultsAnalytics.tests || []}
          pagination={testsQuery.data?.insuranceTestsResultsAnalytics.pagination}
          isLoading={testsQuery.loading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={filterData.dateFrom ? new Date(filterData.dateFrom) : lastWeek}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={filterData.dateTo ? new Date(filterData.dateTo) : today}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <CompaniesDropdown
                value={searchState.data.companyId}
                onChange={(id) => {
                  setFilterData({ ...filterData, companyId: String(id), groupId: '' })
                }}
                hasBottomContent
              />
              <GroupsDropdown
                value={searchState.data.groupId}
                onChange={(id) => {
                  setFilterData({ ...filterData, groupId: String(id) })
                }}
                companyId={searchState.data.companyId}
                hasBottomContent
              />
              <SelectInput
                name="lab"
                label="Lab"
                value={filterData.lab || ''}
                onChange={handleInputChange}
                options={LAB_OPTIONS}
                hasBottomContent
              />
              <Button onClick={getData} size="large">
                Filter
              </Button>
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onRowClick={({ id, eventId }) =>
            eventId
              ? history.push(`/${ROUTES.EVENTS}/${eventId}`)
              : history.push(`/${ROUTES.TESTS}/${id}`)
          }
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={testsQuery.data?.insuranceTestsResultsAnalytics.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default InsuranceAnalyticsTestsResults
